import { normalizeApplicationFeedback } from "./sourceChangeApplicationFeedback.js";
import {
  normalizePortalPublicationDispatchPeriod,
  normalizePortalPublications,
} from "../lib/portalPublicationsApi.js";
import { formatDataManagerAuditSafeFieldValue } from "./dataManagerAuditLabels.js";
import { formatResultsSourceStaleCauseLine } from "./resultsChromeData.js";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function readObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

export function normalizeSourceChangeDomains(value) {
  const rawDomains = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  return [...new Set(rawDomains
    .map((domain) => normalizeInlineText(domain).toLowerCase())
    .filter(Boolean))]
    .sort();
}

const TRUCK_CHANGE_DETAIL_STATUSES = new Set(["actionable", "blocked", "unsupported"]);
const TRUCK_CHANGE_COVERAGE_STATUSES = new Set([
  "complete",
  "incomplete",
  "reference_invalid",
  "reference_required",
  "reference_version_unsupported",
  "unavailable",
]);
const TRUCK_CHANGE_VALUE_VISIBILITIES = new Set(["redacted", "visible"]);
const SENSITIVE_TRUCK_CHANGE_FIELDS = new Set([
  "departure_address",
  "departureaddress",
  "driver_user_id",
  "driveruserid",
  "restrictions",
]);
const SOURCE_STALE_COVERAGE_STATES = new Set(["covered", "uncovered", "unavailable"]);
const SOURCE_STALE_COVERAGE_GAP_CODES = new Set([
  "fleet-source-changes-comparison-incomplete",
  "global-source-change-not-covered",
  "suspended-source-changes-not-optimized",
  "truck-source-change-not-covered",
  "unclassified-source-change",
]);
// Accept the retired backend code during mixed-version deploys without surfacing it as a blocker.
const SOURCE_STALE_COVERAGE_NON_BLOCKING_GAP_CODES = new Set([
  "suspended-source-changes-not-optimized",
]);
const SOURCE_STALE_COVERAGE_DOMAINS = new Set(["fleet", "global", "orders", "unknown"]);
const SOURCE_STALE_COVERAGE_ENTITY_KINDS = new Set(["fleet", "order", "source_change", "truck"]);
const SOURCE_STALE_COVERAGE_REVISION_PATTERN = /^sha256:[a-f0-9]{64}$/i;
const SOURCE_STALE_COVERAGE_FIELD_PATTERN = /^[a-z0-9_.-]{1,80}$/;
const SOURCE_STALE_COVERAGE_ENTITY_ID_PATTERN = /^[a-z0-9:_-]{1,160}$/i;
const SOURCE_STALE_COVERAGE_ENTITY_LABEL_MAX_LENGTH = 160;
const SOURCE_STALE_COVERAGE_CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;

export const SOURCE_STALE_COVERAGE_UNAVAILABLE_LABEL = "Vérifiez Mise à jour; la couverture des écarts n’a pas pu être confirmée.";
const SOURCE_STALE_COVERAGE_GLOBAL_LABEL = "Un réglage de flotte modifié n’est pas couvert par Mise à jour.";
const SOURCE_STALE_COVERAGE_COMPARISON_INCOMPLETE_LABEL = "Les données camion actuelles n’ont pas pu être comparées à celles du dernier calcul.";
const SOURCE_STALE_COVERAGE_UNCLASSIFIED_LABEL = "Un changement de données source a été détecté, mais sa nature n’a pas pu être déterminée.";

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function readAliasedValue(source, snakeCaseKey, camelCaseKey) {
  return hasOwn(source, snakeCaseKey) ? source[snakeCaseKey] : source[camelCaseKey];
}

function normalizeStrictStringList(value, { lowercase = true } = {}) {
  if (!Array.isArray(value)) {
    return null;
  }
  const normalizedValues = [];
  for (const item of value) {
    if (typeof item !== "string") {
      return null;
    }
    const normalizedItem = normalizeInlineText(item);
    if (!normalizedItem) {
      return null;
    }
    normalizedValues.push(lowercase ? normalizedItem.toLowerCase() : normalizedItem);
  }
  return [...new Set(normalizedValues)].sort();
}

function buildSourceStaleCoverageState(state, overrides = {}) {
  return {
    state,
    previewRevision: "",
    gaps: [],
    ...overrides,
  };
}

function normalizeSourceStaleCoverageFieldChange(value) {
  const normalized = normalizeTruckChangedField(value, {
    requireLabel: false,
    requireVisibleValues: true,
    safeValuesOnly: true,
  });
  const field = normalized?.field.toLowerCase() || "";
  if (!normalized || !SOURCE_STALE_COVERAGE_FIELD_PATTERN.test(field)) {
    return null;
  }
  return {
    field,
    valueVisibility: normalized.valueVisibility,
    ...(normalized.valueVisibility === "visible"
      ? { before: normalized.before, after: normalized.after }
      : {}),
  };
}

function normalizeSourceStaleCoverageEntityLabel(value) {
  if (value == null) {
    return null;
  }
  if (typeof value !== "string") {
    return undefined;
  }
  const normalizedValue = value.trim().replace(/\s+/g, " ");
  if (
    !normalizedValue
    || normalizedValue.length > SOURCE_STALE_COVERAGE_ENTITY_LABEL_MAX_LENGTH
    || SOURCE_STALE_COVERAGE_CONTROL_CHARACTER_PATTERN.test(normalizedValue)
  ) {
    return undefined;
  }
  return normalizedValue;
}

function normalizeSourceStaleCoverageGap(value) {
  const source = readObject(value);
  if (!source) {
    return null;
  }
  const code = normalizeInlineText(source.code).toLowerCase();
  const domain = normalizeInlineText(source.domain).toLowerCase();
  const count = source.count;
  const rawEntityKind = readAliasedValue(source, "entity_kind", "entityKind");
  const entityKind = rawEntityKind == null
    ? null
    : normalizeInlineText(rawEntityKind).toLowerCase();
  const rawEntityId = readAliasedValue(source, "entity_id", "entityId");
  const entityId = rawEntityId == null ? null : normalizeInlineText(rawEntityId);
  const rawEntityLabel = readAliasedValue(source, "entity_label", "entityLabel");
  const entityLabel = normalizeSourceStaleCoverageEntityLabel(rawEntityLabel);
  const fields = normalizeStrictStringList(source.fields);
  const rawFieldChanges = readAliasedValue(source, "field_changes", "fieldChanges");
  const fieldChanges = rawFieldChanges == null
    ? []
    : Array.isArray(rawFieldChanges)
      ? rawFieldChanges.map(normalizeSourceStaleCoverageFieldChange)
      : null;
  if (
    !SOURCE_STALE_COVERAGE_GAP_CODES.has(code)
    || !SOURCE_STALE_COVERAGE_DOMAINS.has(domain)
    || !Number.isInteger(count)
    || count <= 0
    || (entityKind !== null && !SOURCE_STALE_COVERAGE_ENTITY_KINDS.has(entityKind))
    || (rawEntityId != null && (
      typeof rawEntityId !== "string"
      || !SOURCE_STALE_COVERAGE_ENTITY_ID_PATTERN.test(entityId)
    ))
    || entityLabel === undefined
    || !fields
    || fields.some((field) => !SOURCE_STALE_COVERAGE_FIELD_PATTERN.test(field))
    || !fieldChanges
    || fieldChanges.some((fieldChange) => !fieldChange)
  ) {
    return null;
  }
  const fieldChangeFields = fieldChanges.map((fieldChange) => fieldChange.field);
  const hasDuplicateFieldChanges = new Set(fieldChangeFields).size !== fieldChangeFields.length;
  const allFieldChangesAreDeclared = fieldChangeFields.every((field) => fields.includes(field));
  const hasExpectedShape = code === "suspended-source-changes-not-optimized"
    ? domain === "orders" && entityKind === "order" && fields.length === 0 && fieldChanges.length === 0
    : code === "global-source-change-not-covered"
      ? (domain === "fleet" || domain === "global")
        && entityKind === "fleet"
        && allFieldChangesAreDeclared
      : code === "fleet-source-changes-comparison-incomplete"
        ? domain === "fleet" && entityKind === "fleet" && fields.length === 0 && fieldChanges.length === 0
        : code === "truck-source-change-not-covered"
          ? domain === "fleet"
            && entityKind === "truck"
            && entityId !== null
            && count === 1
            && fields.length > 0
            && fieldChanges.length === fields.length
            && allFieldChangesAreDeclared
          : code === "unclassified-source-change"
            && domain === "unknown"
            && entityKind === "source_change"
            && fields.length === 0
            && fieldChanges.length === 0;
  if (!hasExpectedShape || hasDuplicateFieldChanges) {
    return null;
  }
  return {
    code,
    domain,
    count,
    entityKind,
    entityId,
    entityLabel,
    fields,
    fieldChanges: [...fieldChanges].sort((left, right) => left.field.localeCompare(right.field)),
  };
}

export function normalizeSourceStaleCoverage(value) {
  if (value == null) {
    return buildSourceStaleCoverageState("legacy_or_absent");
  }
  const source = readObject(value);
  if (!source) {
    return buildSourceStaleCoverageState("unavailable");
  }
  const state = normalizeInlineText(source.state).toLowerCase();
  const previewRevision = normalizeInlineText(
    readAliasedValue(source, "preview_revision", "previewRevision"),
  );
  const rawGaps = source.gaps;
  const gaps = Array.isArray(rawGaps)
    ? rawGaps.map(normalizeSourceStaleCoverageGap)
    : null;
  if (
    !SOURCE_STALE_COVERAGE_STATES.has(state)
    || !SOURCE_STALE_COVERAGE_REVISION_PATTERN.test(previewRevision)
    || !gaps
    || gaps.some((gap) => !gap)
    || (state === "uncovered") !== (gaps.length > 0)
  ) {
    return buildSourceStaleCoverageState("unavailable");
  }
  if (state === "unavailable") {
    return buildSourceStaleCoverageState(state, { previewRevision, gaps });
  }
  const blockingGaps = gaps.filter(
    (gap) => !SOURCE_STALE_COVERAGE_NON_BLOCKING_GAP_CODES.has(gap.code),
  );
  return buildSourceStaleCoverageState(
    blockingGaps.length > 0 ? "uncovered" : "covered",
    { previewRevision, gaps: blockingGaps },
  );
}

function buildSourceStaleCoverageCauseLines(staleCoverage) {
  const lines = staleCoverage.gaps.flatMap((gap) => {
    if (gap.code === "fleet-source-changes-comparison-incomplete") {
      return [SOURCE_STALE_COVERAGE_COMPARISON_INCOMPLETE_LABEL];
    }
    if (gap.code === "global-source-change-not-covered") {
      return [SOURCE_STALE_COVERAGE_GLOBAL_LABEL];
    }
    if (gap.code === "truck-source-change-not-covered") {
      const currentLines = gap.fieldChanges.map((fieldChange) => (
        formatResultsSourceStaleCauseLine({}, {
          entity_kind: "truck",
          entity_id: gap.entityId,
          entity_label: gap.entityLabel,
          field_changes: [{
            field: fieldChange.field,
            values_included: fieldChange.valueVisibility === "visible",
            ...(fieldChange.valueVisibility === "visible"
              ? { old_value: fieldChange.before, new_value: fieldChange.after }
              : {}),
          }],
        })
      )).filter(Boolean);
      return currentLines.length > 0
        ? currentLines
        : [`Camion ${gap.entityLabel || gap.entityId} : données modifiées.`];
    }
    return [SOURCE_STALE_COVERAGE_UNCLASSIFIED_LABEL];
  });
  return [...new Set(lines)];
}

export function resolveDispatchSourceStaleNotice({
  sourceStaleNotice = null,
  sourceChanges = null,
  hasVerifiedPreview = false,
} = {}) {
  if (!sourceStaleNotice || typeof sourceStaleNotice !== "object") {
    return null;
  }
  const genericNotice = { causeLines: [SOURCE_STALE_COVERAGE_UNAVAILABLE_LABEL] };
  if (hasVerifiedPreview !== true) {
    return genericNotice;
  }
  const staleCoverage = sourceChanges?.staleCoverage;
  const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
  if (
    !staleCoverage
    || (staleCoverage.state !== "covered" && staleCoverage.state !== "uncovered")
    || !previewRevision
    || staleCoverage.previewRevision !== previewRevision
  ) {
    return genericNotice;
  }
  if (staleCoverage.state === "covered") {
    return null;
  }
  const causeLines = buildSourceStaleCoverageCauseLines(staleCoverage);
  return causeLines.length > 0 ? { causeLines } : genericNotice;
}

function buildEmptyTruckChangeCounts() {
  return {
    actionable: 0,
    blocked: 0,
    unsupported: 0,
    unresolved: 0,
  };
}

function buildTruckChangesState(state, overrides = {}) {
  return {
    state,
    supported: false,
    enabled: false,
    featureRevision: "",
    coverage: {
      status: state,
      referenceVersion: null,
      reasonCode: null,
    },
    counts: buildEmptyTruckChangeCounts(),
    details: [],
    globalChangedFields: [],
    ...overrides,
  };
}

function buildUnavailableTruckChanges(source = null, coverage = null) {
  return buildTruckChangesState("unavailable", {
    supported: source?.supported === true,
    enabled: source?.enabled === true,
    featureRevision: normalizeInlineText(
      readObject(source)
        ? readAliasedValue(source, "feature_revision", "featureRevision")
        : "",
    ),
    coverage: coverage || {
      status: "unavailable",
      referenceVersion: null,
      reasonCode: "malformed_truck_changes",
    },
  });
}

function normalizeTruckChangeCoverage(value) {
  const source = readObject(value);
  if (!source) {
    return null;
  }
  const status = normalizeInlineText(source.status).toLowerCase();
  if (!TRUCK_CHANGE_COVERAGE_STATUSES.has(status)) {
    return null;
  }
  const rawReferenceVersion = readAliasedValue(source, "reference_version", "referenceVersion");
  const referenceVersion = rawReferenceVersion == null ? null : rawReferenceVersion;
  if (
    referenceVersion !== null
    && (!Number.isInteger(referenceVersion) || referenceVersion < 1)
  ) {
    return null;
  }
  if (status === "complete" && referenceVersion === null) {
    return null;
  }
  const rawReasonCode = readAliasedValue(source, "reason_code", "reasonCode");
  if (rawReasonCode != null && typeof rawReasonCode !== "string") {
    return null;
  }
  const reasonCode = rawReasonCode == null
    ? null
    : normalizeInlineText(rawReasonCode).toLowerCase();
  if (rawReasonCode != null && !reasonCode) {
    return null;
  }
  return { status, referenceVersion, reasonCode };
}

function normalizeTruckChangeCounts(value) {
  const source = readObject(value);
  if (!source) {
    return null;
  }
  const counts = buildEmptyTruckChangeCounts();
  for (const key of Object.keys(counts)) {
    if (!Number.isInteger(source[key]) || source[key] < 0) {
      return null;
    }
    counts[key] = source[key];
  }
  if (counts.unresolved !== counts.actionable + counts.blocked + counts.unsupported) {
    return null;
  }
  return counts;
}

function normalizeTruckChangedField(value, options = {}) {
  const source = readObject(value);
  if (!source) {
    return null;
  }
  const normalizedOptions = readObject(options) || {};
  const field = normalizeInlineText(source.field);
  const label = normalizeInlineText(source.label);
  const valueVisibility = normalizeInlineText(
    readAliasedValue(source, "value_visibility", "valueVisibility"),
  ).toLowerCase();
  if (
    !field
    || (normalizedOptions.requireLabel !== false && !label)
    || !TRUCK_CHANGE_VALUE_VISIBILITIES.has(valueVisibility)
  ) {
    return null;
  }
  const normalizedFieldKey = field.toLowerCase();
  const hasBeforeValue = hasOwn(source, "before");
  const hasAfterValue = hasOwn(source, "after");
  const hasUndefinedValue = (hasBeforeValue && source.before === undefined)
    || (hasAfterValue && source.after === undefined);
  const isSensitive = SENSITIVE_TRUCK_CHANGE_FIELDS.has(normalizedFieldKey);
  if (
    hasUndefinedValue
    || (valueVisibility === "redacted" && (hasBeforeValue || hasAfterValue))
    || (isSensitive && valueVisibility !== "redacted")
    || (
      normalizedOptions.requireVisibleValues === true
      && valueVisibility === "visible"
      && (!hasBeforeValue || !hasAfterValue)
    )
  ) {
    return null;
  }
  if (normalizedOptions.safeValuesOnly === true && valueVisibility === "visible") {
    const beforeLabel = formatDataManagerAuditSafeFieldValue(normalizedFieldKey, source.before);
    const afterLabel = formatDataManagerAuditSafeFieldValue(normalizedFieldKey, source.after);
    if (!beforeLabel || !afterLabel || beforeLabel.length > 120 || afterLabel.length > 120) {
      return null;
    }
  }
  return {
    field,
    label,
    valueVisibility,
    ...(valueVisibility === "visible" && hasBeforeValue ? { before: source.before } : {}),
    ...(valueVisibility === "visible" && hasAfterValue ? { after: source.after } : {}),
  };
}

const TRUCK_REFRESH_RECOVERY_STATUSES = new Set([
  "confirmation_required",
  "insufficient",
  "protected_only",
]);

function normalizeTruckRefreshRecoveryStringList(value) {
  if (!Array.isArray(value)) {
    return null;
  }
  const normalized = value.map(normalizeInlineText);
  if (normalized.some((item) => !item) || new Set(normalized).size !== normalized.length) {
    return null;
  }
  return normalized.sort((left, right) => left.localeCompare(right, "fr", { sensitivity: "base" }));
}

function normalizeTruckRefreshRecoveryMinutes(value, { optional = false } = {}) {
  if (value == null && optional) {
    return null;
  }
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

function normalizeDeliveryClientNameByOrderId(value, orderIds) {
  const source = readObject(value);
  if (!source || !Array.isArray(orderIds) || orderIds.length === 0) {
    return undefined;
  }
  const names = {};
  for (const orderId of orderIds) {
    const name = normalizeInlineText(source[orderId]);
    if (name) {
      names[orderId] = name;
    }
  }
  return Object.keys(names).length > 0 ? names : undefined;
}

function normalizeTruckRefreshRecoveryGroup(value, { constraintKind = "work_duration" } = {}) {
  const source = readObject(value);
  if (!source) {
    return null;
  }
  const sourceGroupKey = normalizeInlineText(
    readAliasedValue(source, "source_group_key", "sourceGroupKey"),
  );
  const groupStepIds = normalizeTruckRefreshRecoveryStringList(
    readAliasedValue(source, "group_step_ids", "groupStepIds"),
  );
  const orderIds = normalizeTruckRefreshRecoveryStringList(
    readAliasedValue(source, "order_ids", "orderIds"),
  );
  const deliveryIds = normalizeTruckRefreshRecoveryStringList(
    readAliasedValue(source, "delivery_ids", "deliveryIds"),
  );
  const label = normalizeInlineText(source.label);
  const commandCount = readAliasedValue(source, "command_count", "commandCount");
	const quantityTons = readAliasedValue(source, "quantity_tons", "quantityTons");
	const selectable = source.selectable;
	const blockedReasonCode = normalizeInlineText(
		readAliasedValue(source, "blocked_reason_code", "blockedReasonCode"),
	).toLowerCase();
	const blockedReason = normalizeInlineText(
		readAliasedValue(source, "blocked_reason", "blockedReason"),
	);
	const loadingSequenceStart = readAliasedValue(
		source, "loading_sequence_start", "loadingSequenceStart",
	);
	const loadingSequenceEnd = readAliasedValue(
		source, "loading_sequence_end", "loadingSequenceEnd",
	);
  const loadingStartAt = normalizeInlineText(
		readAliasedValue(source, "loading_start_at", "loadingStartAt"),
	);
  const deliveryClientNameByOrderId = normalizeDeliveryClientNameByOrderId(
    readAliasedValue(source, "delivery_client_name_by_order_id", "deliveryClientNameByOrderId"),
    orderIds,
  );
  if (
    !sourceGroupKey
    || !groupStepIds
    || !orderIds
    || orderIds.length === 0
    || !deliveryIds
    || deliveryIds.length === 0
    || !label
    || !Number.isInteger(commandCount)
    || commandCount < 1
    || commandCount !== orderIds.length
		|| (
			constraintKind === "load_capacity"
			&& (
				typeof quantityTons !== "number"
				|| !Number.isFinite(quantityTons)
				|| quantityTons <= 0
				|| typeof selectable !== "boolean"
				|| (!selectable && !blockedReasonCode)
				|| (loadingSequenceStart != null && (!Number.isInteger(loadingSequenceStart) || loadingSequenceStart < 1))
				|| (loadingSequenceEnd != null && (!Number.isInteger(loadingSequenceEnd) || loadingSequenceEnd < 1))
				|| (
					loadingSequenceStart != null
					&& loadingSequenceEnd != null
					&& loadingSequenceEnd < loadingSequenceStart
				)
			)
		)
  ) {
    return null;
  }
  return {
    sourceGroupKey,
    groupStepIds,
    orderIds,
    deliveryIds,
    label,
    commandCount,
    ...(deliveryClientNameByOrderId ? { deliveryClientNameByOrderId } : {}),
		...(constraintKind === "load_capacity" ? {
			quantityTons,
			selectable,
			blockedReasonCode,
			blockedReason,
			loadingSequenceStart: loadingSequenceStart ?? null,
			loadingSequenceEnd: loadingSequenceEnd ?? null,
			loadingStartAt,
		} : {}),
  };
}

function normalizeTruckRefreshCapacityCheckpoints(value) {
	if (!Array.isArray(value) || value.length === 0) {
		return null;
	}
	const checkpoints = value.map((rawCheckpoint) => {
		const checkpoint = readObject(rawCheckpoint);
		const checkpointKey = normalizeInlineText(
			readAliasedValue(checkpoint || {}, "checkpoint_key", "checkpointKey"),
		);
		const sequence = checkpoint?.sequence;
		const loadTons = readAliasedValue(checkpoint || {}, "load_tons", "loadTons");
		const rawContributions = checkpoint?.contributions;
		const contributions = Array.isArray(rawContributions)
			? rawContributions.map((rawContribution) => {
				const contribution = readObject(rawContribution);
				const sourceGroupKey = normalizeInlineText(
					readAliasedValue(contribution || {}, "source_group_key", "sourceGroupKey"),
				);
				const quantityTons = readAliasedValue(
					contribution || {}, "quantity_tons", "quantityTons",
				);
				return sourceGroupKey
					&& typeof quantityTons === "number"
					&& Number.isFinite(quantityTons)
					&& quantityTons > 0
					? { sourceGroupKey, quantityTons }
					: null;
			})
			: null;
		if (
			!checkpoint
			|| !checkpointKey
			|| !Number.isInteger(sequence)
			|| sequence < 1
			|| typeof loadTons !== "number"
			|| !Number.isFinite(loadTons)
			|| loadTons < 0
			|| !contributions
			|| contributions.some((item) => !item)
			|| new Set(contributions.map((item) => item.sourceGroupKey)).size !== contributions.length
		) {
			return null;
		}
		return { checkpointKey, sequence, loadTons, contributions };
	});
	return checkpoints.some((checkpoint) => !checkpoint)
		|| new Set(checkpoints.map((checkpoint) => checkpoint.checkpointKey)).size !== checkpoints.length
		? null
		: checkpoints;
}

export function normalizeTruckRefreshRecovery(value) {
  if (value == null) {
    return null;
  }
  const source = readObject(value);
  if (!source) {
    return undefined;
  }
  const status = normalizeInlineText(source.status).toLowerCase();
	const rawConstraintKind = normalizeInlineText(
		readAliasedValue(source, "constraint_kind", "constraintKind"),
	).toLowerCase();
	const constraintKind = rawConstraintKind || (
		Object.prototype.hasOwnProperty.call(source, "required_tons")
		|| Object.prototype.hasOwnProperty.call(source, "requiredTons")
			? "load_capacity"
			: "work_duration"
	);
  const recoveryRevision = normalizeInlineText(
    readAliasedValue(source, "recovery_revision", "recoveryRevision"),
  );
  const requiredMinutes = normalizeTruckRefreshRecoveryMinutes(
    readAliasedValue(source, "required_minutes", "requiredMinutes"),
  );
  const availableMinutes = normalizeTruckRefreshRecoveryMinutes(
    readAliasedValue(source, "available_minutes", "availableMinutes"),
  );
  const projectedRequiredMinutes = normalizeTruckRefreshRecoveryMinutes(
    readAliasedValue(source, "projected_required_minutes", "projectedRequiredMinutes"),
    { optional: true },
  );
	if (!TRUCK_REFRESH_RECOVERY_STATUSES.has(status) || !recoveryRevision) {
		return undefined;
	}
	const rawProposedGroups = readAliasedValue(source, "proposed_groups", "proposedGroups");
  const proposedGroups = Array.isArray(rawProposedGroups)
		? rawProposedGroups.map((group) => normalizeTruckRefreshRecoveryGroup(group, { constraintKind }))
    : null;
	if (constraintKind === "load_capacity") {
		const requiredTons = normalizeTruckRefreshRecoveryMinutes(
			readAliasedValue(source, "required_tons", "requiredTons"),
		);
		const availableTons = normalizeTruckRefreshRecoveryMinutes(
			readAliasedValue(source, "available_tons", "availableTons"),
		);
		const projectedRequiredTons = normalizeTruckRefreshRecoveryMinutes(
			readAliasedValue(source, "projected_required_tons", "projectedRequiredTons"),
			{ optional: true },
		);
		const rawCandidateGroups = readAliasedValue(source, "candidate_groups", "candidateGroups");
		const candidateGroups = Array.isArray(rawCandidateGroups)
			? rawCandidateGroups.map((group) => normalizeTruckRefreshRecoveryGroup(
				group,
				{ constraintKind },
			))
			: null;
		const capacityCheckpoints = normalizeTruckRefreshCapacityCheckpoints(
			readAliasedValue(source, "capacity_checkpoints", "capacityCheckpoints"),
		);
		const candidateByKey = new Map(
			(candidateGroups || []).filter(Boolean).map((group) => [group.sourceGroupKey, group]),
		);
		if (
			requiredTons === undefined
			|| availableTons === undefined
			|| projectedRequiredTons === undefined
			|| requiredTons <= availableTons
			|| !candidateGroups
			|| candidateGroups.some((group) => !group)
			|| candidateByKey.size !== candidateGroups.length
			|| !proposedGroups
			|| proposedGroups.some((group) => !group)
			|| new Set(proposedGroups.map((group) => group.sourceGroupKey)).size !== proposedGroups.length
			|| proposedGroups.some((group) => (
				!candidateByKey.has(group.sourceGroupKey)
				|| candidateByKey.get(group.sourceGroupKey)?.selectable !== true
			))
			|| !capacityCheckpoints
			|| (
				status === "confirmation_required"
				&& (
					projectedRequiredTons == null
					|| candidateGroups.length === 0
				)
			)
		) {
			return undefined;
		}
		return {
			constraintKind,
			status,
			recoveryRevision,
			requiredTons,
			availableTons,
			projectedRequiredTons,
			candidateGroups,
			proposedGroups,
			capacityCheckpoints,
		};
	}
	if (constraintKind !== "work_duration") {
		return undefined;
	}
  if (
		requiredMinutes === undefined
    || availableMinutes === undefined
    || projectedRequiredMinutes === undefined
    || !proposedGroups
    || proposedGroups.some((group) => !group)
    || new Set(proposedGroups.map((group) => group.sourceGroupKey)).size !== proposedGroups.length
    || (
      status === "confirmation_required"
      && (
        projectedRequiredMinutes == null
        || projectedRequiredMinutes > availableMinutes
        || proposedGroups.length === 0
      )
    )
    || requiredMinutes <= availableMinutes
  ) {
    return undefined;
  }
  const proposedOrderIds = proposedGroups.flatMap((group) => group.orderIds);
  const proposedDeliveryIds = proposedGroups.flatMap((group) => group.deliveryIds);
  const proposedStepIds = proposedGroups.flatMap((group) => group.groupStepIds);
  if (
    new Set(proposedOrderIds).size !== proposedOrderIds.length
    || new Set(proposedDeliveryIds).size !== proposedDeliveryIds.length
    || new Set(proposedStepIds).size !== proposedStepIds.length
  ) {
    return undefined;
  }
  return {
		constraintKind,
    status,
    recoveryRevision,
    requiredMinutes,
    availableMinutes,
    projectedRequiredMinutes,
    proposedGroups: proposedGroups.sort((left, right) => left.sourceGroupKey.localeCompare(
      right.sourceGroupKey,
      "fr",
      { sensitivity: "base" },
    )),
  };
}

export function resolveTruckRecoveryPendingDetail(details, pendingTarget) {
  const truckId = normalizeInlineText(pendingTarget?.truckId);
  if (!truckId || !Array.isArray(details)) {
    return null;
  }
  const changeRevision = normalizeInlineText(pendingTarget?.changeRevision);
  const recoveryRevision = normalizeInlineText(pendingTarget?.recoveryRevision);
  const candidates = details.filter((item) => (
    normalizeInlineText(item?.truckId) === truckId
    && item?.status === "blocked"
    && item?.recovery?.status === "confirmation_required"
    && normalizeInlineText(item?.recovery?.recoveryRevision)
  ));
  if (candidates.length === 0) {
    return null;
  }
  return candidates.find((item) => (
    (!changeRevision || normalizeInlineText(item?.changeRevision) === changeRevision)
    && (!recoveryRevision || normalizeInlineText(item?.recovery?.recoveryRevision) === recoveryRevision)
  )) || candidates[0];
}

function normalizeTruckChangeDetails(value) {
  if (!Array.isArray(value)) {
    return null;
  }
  const details = [];
  const truckIds = new Set();
  for (const item of value) {
    const source = readObject(item);
    if (!source) {
      return null;
    }
    const truckId = normalizeInlineText(readAliasedValue(source, "truck_id", "truckId"));
    const truckLabel = normalizeInlineText(readAliasedValue(source, "truck_label", "truckLabel"));
    const changeRevision = normalizeInlineText(
      readAliasedValue(source, "change_revision", "changeRevision"),
    );
    const status = normalizeInlineText(source.status).toLowerCase();
    const rawRetryable = readAliasedValue(source, "retryable", "retryable");
    const retryable = hasOwn(source, "retryable") ? rawRetryable : false;
    const reasonCodes = normalizeStrictStringList(
      readAliasedValue(source, "reason_codes", "reasonCodes"),
    );
    const rawChangedFields = readAliasedValue(source, "changed_fields", "changedFields");
    const changedFields = Array.isArray(rawChangedFields)
      ? rawChangedFields.map(normalizeTruckChangedField)
      : null;
    const rawRecovery = readAliasedValue(source, "recovery", "recovery");
    const recovery = normalizeTruckRefreshRecovery(rawRecovery);
    const lastChangedField = normalizeInlineText(
      readAliasedValue(source, "leading_changed_field", "lastChangedField"),
    );
    // Changement d'attelage du Gantt : menu utilisé (remorque ou camion), camion, remorque et valeur remplacée ;
    // « departed » : ligne quittée par son camion, avec ce camion et sa nouvelle remorque.
    const coupling = readObject(readAliasedValue(source, "coupling_change", "couplingChange"));
    const couplingChange = coupling && ["trailer", "tractor", "departed"].includes(coupling.change)
      && normalizeInlineText(coupling.tractor) && normalizeInlineText(coupling.trailer)
      ? { change: coupling.change, tractor: normalizeInlineText(coupling.tractor), trailer: normalizeInlineText(coupling.trailer),
        previous: normalizeInlineText(coupling.previous) }
      : null;
    if (
      !truckId
      || !changeRevision
      || !TRUCK_CHANGE_DETAIL_STATUSES.has(status)
      || typeof retryable !== "boolean"
      || !reasonCodes
      || !changedFields
      || changedFields.some((field) => !field)
      || (rawRecovery != null && recovery === undefined)
      || truckIds.has(truckId)
    ) {
      return null;
    }
    truckIds.add(truckId);
    const pendingFieldNames = new Set(changedFields.map((field) => field.field));
    const leadingChangedField = pendingFieldNames.has(lastChangedField) ? lastChangedField : "";
    details.push({
      truckId,
      changeRevision,
      status,
      retryable,
      changedFields: changedFields.sort((left, right) => (
        `${left.field}|${left.label}`.localeCompare(`${right.field}|${right.label}`, "fr", {
          sensitivity: "base",
        })
      )),
      reasonCodes,
      applicationFeedback: normalizeApplicationFeedback(source.application_feedback || source.applicationFeedback),
      ...(truckLabel && !/^TRK[-_]/i.test(truckLabel) ? { truckLabel } : {}),
      ...(recovery ? { recovery } : {}),
      ...(leadingChangedField ? { lastChangedField: leadingChangedField } : {}),
      ...(couplingChange ? { couplingChange } : {}),
      ...(source.dompeur === true ? { dompeur: true } : {}),
    });
  }
  return details.sort((left, right) => (
    `${left.truckId}|${left.changeRevision}`.localeCompare(
      `${right.truckId}|${right.changeRevision}`,
      "fr",
      { sensitivity: "base" },
    )
  ));
}

export function normalizeTruckChanges(value) {
  if (value == null) {
    return buildTruckChangesState("legacy_or_absent");
  }
  const source = readObject(value);
  if (
    !source
    || typeof source.supported !== "boolean"
    || typeof source.enabled !== "boolean"
  ) {
    return buildUnavailableTruckChanges(source);
  }
  const featureRevision = normalizeInlineText(
    readAliasedValue(source, "feature_revision", "featureRevision"),
  );
  if (!featureRevision || source.supported !== true) {
    return buildUnavailableTruckChanges(source);
  }
  if (source.enabled === false) {
    return buildTruckChangesState("disabled", {
      supported: true,
      featureRevision,
      coverage: {
        status: "disabled",
        referenceVersion: null,
        reasonCode: null,
      },
    });
  }

  const coverage = normalizeTruckChangeCoverage(source.coverage);
  const counts = normalizeTruckChangeCounts(source.counts);
  const details = normalizeTruckChangeDetails(source.details);
  const rawGlobalChangedFields = readAliasedValue(
    source,
    "global_changed_fields",
    "globalChangedFields",
  );
  const globalChangedFields = rawGlobalChangedFields === undefined
    ? []
    : Array.isArray(rawGlobalChangedFields)
      ? rawGlobalChangedFields.map(normalizeTruckChangedField)
      : null;
  if (
    !coverage
    || !counts
    || !details
    || !globalChangedFields
    || globalChangedFields.some((field) => !field)
  ) {
    return buildUnavailableTruckChanges(source, coverage);
  }
  const detailCounts = details.reduce((result, detail) => ({
    ...result,
    [detail.status]: result[detail.status] + 1,
  }), { actionable: 0, blocked: 0, unsupported: 0 });
  if (
    detailCounts.actionable !== counts.actionable
    || detailCounts.blocked !== counts.blocked
    || detailCounts.unsupported !== counts.unsupported
  ) {
    return buildUnavailableTruckChanges(source, coverage);
  }
  if (coverage.status === "reference_required") {
    if (counts.unresolved !== 0 || details.length !== 0 || globalChangedFields.length !== 0) {
      return buildUnavailableTruckChanges(source, coverage);
    }
    return buildTruckChangesState("reference_required", {
      supported: true,
      enabled: true,
      featureRevision,
      coverage,
    });
  }
  if (coverage.status !== "complete") {
    return buildUnavailableTruckChanges(source, coverage);
  }
  return buildTruckChangesState("ready", {
    supported: true,
    enabled: true,
    featureRevision,
    coverage,
    counts,
    details,
    globalChangedFields: globalChangedFields.sort((left, right) => (
      `${left.field}|${left.label}`.localeCompare(`${right.field}|${right.label}`, "fr", {
        sensitivity: "base",
      })
    )),
  });
}

function normalizeLoadingConstraintFieldChange(value) {
  const source = readObject(value);
  const field = normalizeInlineText(source?.field);
  if (!source || !field) {
    return null;
  }
  const valueChanges = (Array.isArray(source.value_changes || source.valueChanges)
    ? (source.value_changes || source.valueChanges)
    : []).map((rawChange) => {
    const change = readObject(rawChange);
    return change ? {
      dispatchValue: readAliasedValue(change, "dispatch_value", "dispatchValue"),
      sourceValue: readAliasedValue(change, "source_value", "sourceValue"),
      orderCount: Number(change.order_count || change.orderCount || 0),
    } : null;
  }).filter(Boolean);
  return {
    field,
    orderCount: Number(source.order_count || source.orderCount || 0),
    dispatchValue: readAliasedValue(source, "dispatch_value", "dispatchValue"),
    sourceValue: readAliasedValue(source, "source_value", "sourceValue"),
    valueChanges,
  };
}

export function normalizeLoadingConstraintChanges(value) {
  const source = readObject(value);
  const rawDetails = Array.isArray(source?.details) ? source.details : [];
  const details = rawDetails.map((rawDetail) => {
    const detail = readObject(rawDetail);
    const truckId = normalizeInlineText(detail?.truck_id || detail?.truckId);
    const changeRevision = normalizeInlineText(detail?.change_revision || detail?.changeRevision);
    if (!detail || !truckId || !changeRevision) {
      return null;
    }
    const mills = (Array.isArray(detail.mills) ? detail.mills : []).map((rawMill) => {
      const mill = readObject(rawMill);
      const name = normalizeInlineText(mill?.name);
      if (!mill || !name) {
        return null;
      }
      return {
        name,
        loadingAddress: normalizeInlineText(mill.loading_address || mill.loadingAddress),
        orderCount: Number(mill.order_count || mill.orderCount || 0),
        orderIds: Array.isArray(mill.order_ids || mill.orderIds)
          ? (mill.order_ids || mill.orderIds).map(normalizeInlineText).filter(Boolean)
          : [],
        changedFields: Array.isArray(mill.changed_fields || mill.changedFields)
          ? (mill.changed_fields || mill.changedFields).map(normalizeInlineText).filter(Boolean)
          : [],
        fieldChanges: (Array.isArray(mill.field_changes || mill.fieldChanges)
          ? (mill.field_changes || mill.fieldChanges)
          : []).map(normalizeLoadingConstraintFieldChange).filter(Boolean),
      };
    }).filter(Boolean);
    const conflicts = (Array.isArray(detail.conflicts) ? detail.conflicts : []).map((rawConflict) => {
      const conflict = readObject(rawConflict);
      if (!conflict) {
        return null;
      }
      return {
        orderId: normalizeInlineText(conflict.order_id || conflict.orderId),
        deliveryId: normalizeInlineText(conflict.delivery_id || conflict.deliveryId),
        plannedTime: normalizeInlineText(conflict.planned_time || conflict.plannedTime),
        newLimit: conflict.new_limit ?? conflict.newLimit ?? null,
        constraintType: normalizeInlineText(conflict.constraint_type || conflict.constraintType),
        reasonCode: normalizeInlineText(conflict.reason_code || conflict.reasonCode),
        message: normalizeInlineText(conflict.message),
      };
    }).filter(Boolean);
    return {
      truckId,
      truckLabel: normalizeInlineText(detail.truck_label || detail.truckLabel) || truckId,
      changeRevision,
      orderCount: Number(detail.order_count || detail.orderCount || 0),
      orderIds: Array.isArray(detail.order_ids || detail.orderIds)
        ? (detail.order_ids || detail.orderIds).map(normalizeInlineText).filter(Boolean)
        : [],
      deliveryIds: Array.isArray(detail.delivery_ids || detail.deliveryIds)
        ? (detail.delivery_ids || detail.deliveryIds).map(normalizeInlineText).filter(Boolean)
        : [],
      changedFields: Array.isArray(detail.changed_fields || detail.changedFields)
        ? (detail.changed_fields || detail.changedFields).map(normalizeInlineText).filter(Boolean)
        : [],
      mills,
      conflicts,
      applicationFeedback: normalizeApplicationFeedback(detail.application_feedback || detail.applicationFeedback),
    };
  }).filter(Boolean);
  const counts = readObject(source?.counts);
  return {
    counts: {
      trucks: Number(counts?.trucks || details.length),
      orders: Number(counts?.orders || details.reduce((sum, detail) => sum + detail.orderCount, 0)),
      mills: Number(counts?.mills || 0),
      unresolved: Number(counts?.unresolved || details.reduce((sum, detail) => sum + detail.orderCount, 0)),
    },
    details,
  };
}

function buildSourceChangeResolutionState(state, overrides = {}) {
  return {
    state,
    coveredChangeDomains: [],
    unresolvedChangeDomains: [],
    requiresOptimization: state === "unavailable" ? true : null,
    reasonCodes: state === "unavailable" ? ["malformed_resolution"] : [],
    ...overrides,
  };
}

export function normalizeSourceChangeResolution(value) {
  if (value == null) {
    return buildSourceChangeResolutionState("legacy_or_absent");
  }
  const source = readObject(value);
  if (!source) {
    return buildSourceChangeResolutionState("unavailable");
  }
  const coveredChangeDomains = normalizeStrictStringList(
    readAliasedValue(source, "covered_change_domains", "coveredChangeDomains"),
  );
  const unresolvedChangeDomains = normalizeStrictStringList(
    readAliasedValue(source, "unresolved_change_domains", "unresolvedChangeDomains"),
  );
  const requiresOptimization = readAliasedValue(
    source,
    "requires_optimization",
    "requiresOptimization",
  );
  const reasonCodes = normalizeStrictStringList(
    readAliasedValue(source, "reason_codes", "reasonCodes"),
  );
  if (
    !coveredChangeDomains
    || !unresolvedChangeDomains
    || typeof requiresOptimization !== "boolean"
    || !reasonCodes
    || unresolvedChangeDomains.some((domain) => !coveredChangeDomains.includes(domain))
    || (unresolvedChangeDomains.length > 0 && requiresOptimization !== true)
  ) {
    return buildSourceChangeResolutionState("unavailable");
  }
  return buildSourceChangeResolutionState("ready", {
    coveredChangeDomains,
    unresolvedChangeDomains,
    requiresOptimization,
    reasonCodes,
  });
}

export function hasFullyActionableTruckSourceChanges(sourceChanges, hasVerifiedPreview) {
  if (hasVerifiedPreview !== true) {
    return false;
  }

  const truckChanges = sourceChanges?.truckChanges;
  const counts = truckChanges?.counts;
  const resolution = sourceChanges?.resolution;
  const unresolvedChangeDomains = resolution?.unresolvedChangeDomains;
  return truckChanges?.enabled === true
    && truckChanges?.state === "ready"
    && truckChanges?.coverage?.status === "complete"
    && Number.isInteger(counts?.actionable)
    && counts.actionable > 0
    && counts.blocked === 0
    && counts.unsupported === 0
    && counts.unresolved === counts.actionable
    && Array.isArray(truckChanges.details)
    && truckChanges.details.length === counts.actionable
    && truckChanges.details.every((detail) => detail?.status === "actionable")
    && Array.isArray(truckChanges.globalChangedFields)
    && truckChanges.globalChangedFields.length === 0
    && resolution?.state === "ready"
    && resolution.requiresOptimization === true
    && Array.isArray(unresolvedChangeDomains)
    && unresolvedChangeDomains.includes("fleet");
}

export function hasOnlyActionableTruckSourceChanges(sourceChanges, hasVerifiedPreview) {
  const unresolvedChangeDomains = sourceChanges?.resolution?.unresolvedChangeDomains;
  return hasFullyActionableTruckSourceChanges(sourceChanges, hasVerifiedPreview)
    && Array.isArray(unresolvedChangeDomains)
    && unresolvedChangeDomains.length === 1
    && unresolvedChangeDomains[0] === "fleet";
}

export function areUnresolvedSourceChangeDomainsCoveredByUpdate(
  unresolvedChangeDomains,
  { fleet = false, orders = false } = {},
) {
  return Array.isArray(unresolvedChangeDomains)
    && unresolvedChangeDomains.length > 0
    && unresolvedChangeDomains.every((domain) => (
      domain === "orders"
        ? orders === true
        : domain === "fleet" && fleet === true
    ));
}

export function normalizeSourceChangeMetadataSync(value, fallbackPreviewRevision = "") {
  const source = readObject(value) || {};
  const details = [
    ...(Array.isArray(source.details) ? source.details : []),
    ...(Array.isArray(source.changes) ? source.changes : []),
    ...(Array.isArray(source.items) ? source.items : []),
  ].filter(Boolean);
  const explicitOrderIds = Array.isArray(source.order_ids)
    ? source.order_ids
    : Array.isArray(source.orderIds)
      ? source.orderIds
      : [];
  const orderIds = [...new Set([
    ...explicitOrderIds.map(normalizeInlineText),
    ...details.map((detail) => normalizeInlineText(detail?.order_id || detail?.orderId)),
  ].filter(Boolean))].sort();

  return {
    details,
    orderIds,
    previewRevision: normalizeInlineText(
      source.preview_revision
        || source.previewRevision
        || fallbackPreviewRevision,
    ),
  };
}

function buildEmptySourceChangeCounts() {
  return {
    new: 0,
    modified_same_date: 0,
    loading_constraint_changes: 0,
    date_changed_remove: 0,
    missing_dispatch_orders: 0,
    removed_end_of_day_loads: 0,
    protected_execution: 0,
    blocking_protected_execution: 0,
    inconsistent_execution_removals: 0,
    integrity_conflicts: 0,
    metadata_sync: 0,
    total: 0,
    unresolved_total: 0,
  };
}

function buildEmptySourceChangeDetails() {
  return {
    new: [],
    modified_same_date: [],
    loading_constraint_changes: [],
    date_changed_remove: [],
    missing_dispatch_orders: [],
    removed_end_of_day_loads: [],
    protected_execution: [],
    inconsistent_execution_removals: [],
    integrity_conflicts: [],
  };
}

export function buildResultsLatestBootstrapApiUrl(search = "", { deferSourceChanges = false } = {}) {
  const searchParams = new URLSearchParams(typeof search === "string" ? search : "");
  searchParams.set("include_dispatch_bootstrap", "1");
  // Seul le Dispatch ouvre la période (tracteurs et chauffeurs repris) ; les Rapports ne l'envoient pas.
  searchParams.set("open_dispatch_period", "1");
  if (deferSourceChanges) {
    // « Mise à jour » et publications arrivent ensuite par leurs propres chargements.
    searchParams.set("defer_source_changes", "1");
  }
  return `/api/results/latest?${searchParams.toString()}`;
}

export function readInitialPortalPublications(
  initialResultsData = null,
  { dispatchDate = "", dispatchPeriod = "day" } = {},
) {
  const source = readObject(initialResultsData?.portal_publications);
  const activeDispatchDate = normalizeInlineText(dispatchDate);
  const activeDispatchPeriod = normalizePortalPublicationDispatchPeriod(dispatchPeriod);
  const sourceDispatchDate = normalizeInlineText(source?.dispatch_date || source?.dispatchDate);
  const sourceDispatchPeriod = normalizeInlineText(source?.dispatch_period || source?.dispatchPeriod);

  if (
    !source
    || !activeDispatchDate
    || !sourceDispatchDate
    || !sourceDispatchPeriod
    || sourceDispatchDate !== activeDispatchDate
    || normalizePortalPublicationDispatchPeriod(sourceDispatchPeriod) !== activeDispatchPeriod
  ) {
    return null;
  }

  return normalizePortalPublications(source);
}

export function readInitialDispatchSnapshots(
  initialResultsData = null,
  { dispatchDate = "", dispatchPeriod = "day" } = {},
) {
  const source = readObject(initialResultsData?.dispatch_snapshots);
  const activeDispatchDate = normalizeInlineText(dispatchDate);
  const activeDispatchPeriod = normalizePortalPublicationDispatchPeriod(dispatchPeriod);
  const sourceDispatchDate = normalizeInlineText(source?.dispatch_date || source?.dispatchDate);
  const sourceDispatchPeriod = normalizeInlineText(source?.dispatch_period || source?.dispatchPeriod);

  if (!source || !activeDispatchDate || sourceDispatchDate !== activeDispatchDate) {
    return null;
  }
  if (
    sourceDispatchPeriod
    && normalizePortalPublicationDispatchPeriod(sourceDispatchPeriod) !== activeDispatchPeriod
  ) {
    return null;
  }

  return {
    dispatchDate: sourceDispatchDate,
    dispatchPeriod: sourceDispatchPeriod
      ? normalizePortalPublicationDispatchPeriod(sourceDispatchPeriod)
      : "",
    items: Array.isArray(source.items) ? source.items.filter(Boolean) : [],
  };
}

export function readInitialSourceChanges(
  initialResultsData = null,
  { dispatchDate = "", dispatchPeriod = "day" } = {},
) {
  const source = readObject(initialResultsData?.source_changes);
  const activeDispatchDate = normalizeInlineText(dispatchDate);
  const activeDispatchPeriod = normalizePortalPublicationDispatchPeriod(dispatchPeriod);
  const sourceDispatchDate = normalizeInlineText(source?.dispatch_date || source?.dispatchDate);
  const sourceDispatchPeriod = normalizeInlineText(source?.dispatch_period || source?.dispatchPeriod);

  if (
    !source
    || !activeDispatchDate
    || !sourceDispatchDate
    || !sourceDispatchPeriod
    || sourceDispatchDate !== activeDispatchDate
    || normalizePortalPublicationDispatchPeriod(sourceDispatchPeriod) !== activeDispatchPeriod
  ) {
    return null;
  }

  const endOfDayLoadIntegrity = readObject(
    source.end_of_day_load_integrity || source.endOfDayLoadIntegrity,
  ) || { status: "ok", counts: { checked: 0, blocking: 0 }, preview_revision: "", issues: [] };
  const truckOutputPolicy = readObject(
    source.truck_output_policy
      || source.truckOutputPolicy
      || initialResultsData?.truck_output_policy
      || initialResultsData?.truckOutputPolicy,
  ) || null;
  const sourceDataState = readObject(source.source_data_state || source.sourceDataState);
  const previewRevision = normalizeInlineText(source.preview_revision || source.previewRevision);
  const mutationPreviewRevision = normalizeInlineText(
    source.mutation_preview_revision
      || source.mutationPreviewRevision
      || previewRevision,
  );
  const runtimeRevision = normalizeInlineText(
    source.runtime_revision
      || source.runtimeRevision
      || initialResultsData?.runtime_revision
      || initialResultsData?.runtimeRevision,
  );
  const truckChanges = normalizeTruckChanges(
    readAliasedValue(source, "truck_changes", "truckChanges"),
  );
  const loadingConstraintChanges = normalizeLoadingConstraintChanges(
    readAliasedValue(source, "loading_constraint_changes", "loadingConstraintChanges"),
  );
  let resolution = normalizeSourceChangeResolution(source.resolution);
  if (
    truckChanges.state === "unavailable"
    || (
      truckChanges.state !== "legacy_or_absent"
      && resolution.state !== "ready"
    )
    || (
      (truckChanges.state === "ready" || truckChanges.state === "reference_required")
      && !resolution.coveredChangeDomains.includes("fleet")
    )
    || (
      truckChanges.state === "reference_required"
      && resolution.requiresOptimization !== true
    )
    || (
      truckChanges.state === "ready"
      && (truckChanges.counts.unresolved > 0 || truckChanges.globalChangedFields.length > 0)
      && (
        resolution.requiresOptimization !== true
        || !resolution.unresolvedChangeDomains.includes("fleet")
      )
    )
  ) {
    resolution = buildSourceChangeResolutionState("unavailable");
  }

  return {
    dispatchDate: sourceDispatchDate,
    dispatchPeriod: normalizePortalPublicationDispatchPeriod(sourceDispatchPeriod),
    counts: readObject(source.counts) || buildEmptySourceChangeCounts(),
    details: readObject(source.details) || buildEmptySourceChangeDetails(),
    changeDomains: normalizeSourceChangeDomains(
      source.change_domains
        || source.changeDomains
        || sourceDataState?.change_domains
        || sourceDataState?.changeDomains,
    ),
    endOfDayLoadIntegrity,
    metadataSync: normalizeSourceChangeMetadataSync(
      source.metadata_sync || source.metadataSync,
      mutationPreviewRevision,
    ),
    mutationPreviewRevision,
    previewRevision,
    runtimeRevision,
    staleCoverage: normalizeSourceStaleCoverage(
      readAliasedValue(source, "stale_coverage", "staleCoverage"),
    ),
    loadingConstraintChanges,
    // Catégorie EXP de Mise à jour (camions hors limite de la date), détails bruts du backend.
    compartmentFillViolations: readObject(readAliasedValue(source, "compartment_fill_violations", "compartmentFillViolations")) || { details: [] },
    truckChanges,
    resolution,
    suspendableErrors: readObject(source.suspendable_errors || source.suspendableErrors) || {
      counts: { total: 0 },
      details: [],
    },
    truckOutputPolicy,
  };
}

function sourceChangeCountsMatch(counts, remainingCounts) {
  if (!remainingCounts) {
    return true;
  }

  const keys = new Set([...Object.keys(counts), ...Object.keys(remainingCounts)]);
  return [...keys].every((key) => {
    const count = Number(counts[key]);
    const remainingCount = Number(remainingCounts[key]);
    return Number.isFinite(count)
      && Number.isFinite(remainingCount)
      && count === remainingCount;
  });
}

export function readSourceChangesMutationResponse(
  payload = null,
  { dispatchDate = "", dispatchPeriod = "day" } = {},
) {
  if (payload?.source_changes_refresh_required === true) return null;
  const source = readObject(payload?.source_changes);
  const counts = readObject(source?.counts);
  const details = readObject(source?.details);
  const remainingCounts = readObject(source?.remaining_counts || source?.remainingCounts);
  const previewRevision = normalizeInlineText(source?.preview_revision || source?.previewRevision);
  const mutationPreviewRevision = normalizeInlineText(
    source?.mutation_preview_revision
      || source?.mutationPreviewRevision
      || previewRevision,
  );
  const metadataPreviewRevision = normalizeInlineText(
    source?.metadata_sync?.preview_revision
      || source?.metadataSync?.previewRevision,
  );

  if (
    !source
    || !counts
    || !details
    || !previewRevision
    || !mutationPreviewRevision
    || (metadataPreviewRevision && metadataPreviewRevision !== mutationPreviewRevision)
    || !sourceChangeCountsMatch(counts, remainingCounts)
  ) {
    return null;
  }

  const sourceChanges = readInitialSourceChanges(
    { source_changes: source },
    { dispatchDate, dispatchPeriod },
  );
  return sourceChanges?.previewRevision === previewRevision
    && sourceChanges?.mutationPreviewRevision === mutationPreviewRevision
    ? sourceChanges
    : null;
}
